import { buildAtlAttributionHeaders } from '@atlassian/search-client/atl-attribution';
import { type MercuryFocusArea, mercuryFocusAreasQuery } from '@atlassian/search-client/focusAreas';

import { type CloudConfig } from '../../../../../common/constants/filters/cloud-config/types';
import {
	type FetchedSelectFilterOption,
	type SelectFilterState,
} from '../../../../../common/constants/filters/select-filter/types';
import { ProductKeys, type ProductKeys1P } from '../../../../../common/constants/product-keys';
import { createMultiSelectFilter } from '../../../../filters/multi-select-filter';

// Number of results returned in dropdown
const FOCUS_AREA_RESULTS_LIMIT = 20;

const mapFocusAreaResult = (
	focusArea: MercuryFocusArea,
	product: ProductKeys,
): FetchedSelectFilterOption => {
	return {
		trackingKey: focusArea.node?.uuid || '',
		label: focusArea.node?.name || '',
		value: focusArea.node?.uuid || '',
		queryParamValue: focusArea.node?.uuid || '',
		metadata: {
			type: focusArea.node?.focusAreaType.name,
			owner: focusArea.node?.owner?.name,
			status: focusArea.node?.status?.key,
			health: focusArea.node?.health?.key,
		},
		product: product,
	};
};

/**
 * Queries focus areas by search term
 * @param query
 * @param config
 * @returns
 */
const searchPositionFocusAreas = async (
	query: string,
	config: CloudConfig,
): Promise<FetchedSelectFilterOption[]> => {
	const mqlQuery = `(search LIKE '${query}')`;
	const headers = buildAtlAttributionHeaders({
		cloudId: config.cloudId,
		activationId: config.activationId,
		product: config.primaryProduct,
	});
	const focusAreasResponse = await mercuryFocusAreasQuery(
		config.cloudId || '',
		mqlQuery,
		FOCUS_AREA_RESULTS_LIMIT,
		undefined,
		headers,
	);
	const focusAreas: MercuryFocusArea[] = focusAreasResponse.data?.mercury.focusAreas.edges ?? [];

	return focusAreas.map((focusArea) =>
		mapFocusAreaResult(focusArea, config.product as ProductKeys),
	);
};

/**
 * Queries all selected focus areas by id from query params
 * @param queryParams
 * @param config
 * @returns
 */
const getOptionsWithSelectionsFromQueryParams = async (
	queryParams: string[],
	config: CloudConfig,
): Promise<FetchedSelectFilterOption[]> => {
	const mqlQuery = `(${queryParams.map((id) => `id = '${id}'`).join(' OR ')})`;
	const headers = buildAtlAttributionHeaders({
		cloudId: config.cloudId,
		activationId: config.activationId,
		product: config.primaryProduct,
	});
	const focusAreasResponse = await mercuryFocusAreasQuery(
		config.cloudId || '',
		mqlQuery,
		FOCUS_AREA_RESULTS_LIMIT,
		undefined,
		headers,
	);
	const focusAreas: MercuryFocusArea[] = focusAreasResponse.data?.mercury.focusAreas.edges ?? [];

	return focusAreas.map((focusArea) =>
		mapFocusAreaResult(focusArea, config.product as ProductKeys),
	);
};

const productKeys1P: ProductKeys1P[] = [ProductKeys.Talent];

export const filter: Readonly<{
	id: 'positionFocusArea';
	type: 'single-select' | 'multi-select';
	queryParams: {
		key: string;
		getState: (
			queryParams: Record<string, string[]>,
			cloudId: string,
		) => SelectFilterState<'positionFocusArea'>;
	};
	products: ProductKeys[];
	universal: boolean;
	customValueFields?: string[];
	withState: (
		state: unknown,
		config: CloudConfig,
	) => import('../../../../../common/constants/filters/select-filter/types').SelectFilterMonad<'positionFocusArea'>;
	getInitialState: (
		config: CloudConfig,
	) => import('../../../../../common/constants/filters/select-filter/types').SelectFilterMonad<'positionFocusArea'>;
	isCompatible: (filterId?: string) => boolean;
	isSupportCustomValue: boolean;
	isSuggestedFilter?: (primaryProduct?: string) => boolean;
}> = createMultiSelectFilter({
	id: 'positionFocusArea',
	products: productKeys1P,
	alwaysVisible: false,
	universal: false,
	queryParams: {
		key: 'positionFocusAreas',
		fetcher: getOptionsWithSelectionsFromQueryParams,
	},
	initialOptions: {
		fetcher: async (config) => {
			return searchPositionFocusAreas('', config);
		},
	},
	lookup: {
		fetcher: searchPositionFocusAreas,
	},
});
